import { useEffect } from 'react';
import {
  WebsocketAction,
  LastWebsocketJsonMessageType,
  IEventSettingsUpdatedPayload,
} from '@interfaces/webSocketConnectionInfo';
import { useMytaverseEvent } from '@core/dashboard/providers';

/**
 * Applies an `EVENT_SETTINGS_UPDATED` broadcast to the loaded experience.
 *
 * `currentEvent` is fetched once on entering the experience, so without this the
 * only way a CMS settings change reaches the hub is a full page reload.
 *
 * @param lastJsonMessage - Latest websocket message; ignored unless it is an
 *   `EVENT_SETTINGS_UPDATED` broadcast.
 */
export const useHandleEventSettingsMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { setCurrentEvent } = useMytaverseEvent();

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action } = lastJsonMessage;

    if (action !== WebsocketAction.EventSettingsUpdated) {
      return;
    }

    const { settings, features, mapFeatureFlags } =
      lastJsonMessage as IEventSettingsUpdatedPayload;

    if (!settings && !features && !mapFeatureFlags) {
      return;
    }

    setCurrentEvent((prev) => {
      if (!prev) {
        return prev;
      }

      return {
        ...prev,
        // A plain spread of `settings` carries an explicit falsy value through —
        // e.g. clearing `aiVirtualAssistantName` to `''` — so dropping falsy
        // keys here would make that a no-op in the hub, which is the case that
        // has to work. The AI enable/AMA toggles no longer live in `settings`;
        // they ride the `features` merge below like every other feature flag.
        settings: {
          ...prev.settings,
          ...settings,
          ...(features ? { features } : {}),
          ...(mapFeatureFlags ? { mapFeatureFlags } : {}),
        },
      };
    });
  }, [lastJsonMessage, setCurrentEvent]);
};
